// Carrier display row — extracted from LogisticsCarriersList.tsx as the
// D3j prep split (the D3h tracker entry flagged this file would need
// splitting before the next slice touched it; D3j adds a per-row Rigs
// toggle that's the natural cut). Pure presentation component: takes the
// carrier + the panel hook + the two sibling sub-card toggles (Routes,
// Rigs), wires the row buttons. All state lives upstream.
import {
  CARRIER_KIND_CHIP_CLASSES,
  CARRIER_KIND_LABELS,
  classifyExpiry,
  expiryClass,
  formatCoverageAreas,
  ghostBtnClass,
} from "./logisticsCarriersShared";
import type { useLogisticsCarriersPanel } from "./useLogisticsCarriersPanel";
import type { LogiCarrier } from "@/lib/logistics";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export type CarrierRowExpansion = "routes" | "rigs";

type Props = {
  carrier: LogiCarrier;
  m: ReturnType<typeof useLogisticsCarriersPanel>;
  expanded: CarrierRowExpansion | null;
  onToggle: (kind: CarrierRowExpansion) => void;
};

export function LogisticsCarriersDisplayRow({ carrier, m, expanded, onToggle }: Props) {
  const kindChipClass = CARRIER_KIND_CHIP_CLASSES[carrier.carrier_kind];
  const insurance = classifyExpiry(carrier.insurance_expires_at);
  const contract = classifyExpiry(carrier.contract_expires_at);
  const coverage = formatCoverageAreas(carrier.coverage_areas);
  return (
    <li className="flex flex-wrap items-start gap-2 py-3">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
          {carrier.name}
          <span
            className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${kindChipClass}`}
          >
            {CARRIER_KIND_LABELS[carrier.carrier_kind]}
          </span>
          {!carrier.is_active ? (
            <span className="rounded-md bg-gray-400 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              Inactive
            </span>
          ) : null}
        </div>
        {carrier.contact_name || carrier.contact_email || carrier.contact_phone ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">
            {[carrier.contact_name, carrier.contact_email, carrier.contact_phone]
              .filter(Boolean)
              .join(" · ")}
          </div>
        ) : null}
        {coverage ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">Coverage: {coverage}</div>
        ) : null}
        {carrier.capacity_linear_feet !== null ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">
            Capacity: {carrier.capacity_linear_feet} ft
          </div>
        ) : null}
        {insurance.kind !== "none" ? (
          <div className={`text-xs ${expiryClass(insurance.kind)}`}>
            Insurance: {insurance.label}
          </div>
        ) : null}
        {contract.kind !== "none" ? (
          <div className={`text-xs ${expiryClass(contract.kind)}`}>
            Contract: {contract.label}
          </div>
        ) : null}
        {carrier.notes ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">{carrier.notes}</div>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => onToggle("rigs")}
          disabled={m.actionBusy}
        >
          {expanded === "rigs" ? "Hide rigs" : "Rigs"}
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => onToggle("routes")}
          disabled={m.actionBusy}
        >
          {expanded === "routes" ? "Hide routes" : "Routes"}
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => m.startEdit(carrier)}
          disabled={m.actionBusy}
        >
          Edit
        </button>
        <button
          type="button"
          {...{ [FIREFLY_TARGET_ATTR]: "logi-carrier-active-toggle" }}
          className={ghostBtnClass}
          onClick={() => void m.toggleActive(carrier)}
          disabled={m.actionBusy}
        >
          {carrier.is_active ? "Deactivate" : "Reactivate"}
        </button>
      </div>
    </li>
  );
}
